import type {
  MaltaFullTimeFeeLineKey,
  MaltaFullTimeFeeVisibility,
} from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";
import { MALTA_FULL_TIME_FEE_VISIBILITY_FIELD } from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";
import type { MaltaFullTimeNoticeKey } from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";

export type MaltaDistanceFeeLineKey = "tuitionFee" | "admissionFee" | "otherExpenses" | "certificationEqualization";

export type MaltaDistanceFeeVisibility = Record<MaltaDistanceFeeLineKey, boolean>;

export const MALTA_DISTANCE_FEE_LINE_ORDER: MaltaDistanceFeeLineKey[] = [
  "tuitionFee",
  "admissionFee",
  "otherExpenses",
  "certificationEqualization",
];

export const MALTA_DISTANCE_FEE_LINE_SUFFIXES: Record<MaltaDistanceFeeLineKey, string> = {
  tuitionFee: " EUR (per year)",
  admissionFee: " EUR one-time payment",
  otherExpenses: " EUR first year only",
  certificationEqualization: " EUR (first year only)",
};

export const MALTA_DISTANCE_FEE_LINE_LABELS: Record<MaltaDistanceFeeLineKey, string> = {
  tuitionFee: "Tuition Fee",
  admissionFee: "Admission Fee",
  otherExpenses: "Other Expenses (Administrative Fee, Registration Fee, etc.)",
  certificationEqualization: "Certification and Equalization of all documents",
};

export const formatMaltaDistanceFeeLineDisplay = (key: MaltaDistanceFeeLineKey, numericValue?: string) => {
  const trimmed = numericValue?.trim();
  if (!trimmed) return "";
  return `${trimmed}${MALTA_DISTANCE_FEE_LINE_SUFFIXES[key]}`;
};

export const MALTA_DISTANCE_STATIC_ADMISSION_PARAGRAPH =
  "This Admission Letter confirms your eligibility to study in International European University. To continue the admission process and to enroll to the university, you need to cover the complete first year fee. After the payment is received you will be officially accepted to the university and provided with your personal Student\u2019s Office. There you will have an access to your personalized curriculum, all the educational materials, lectures, textbooks, etc. The classes will be provided according to your schedule and curriculum.";

export const MALTA_DISTANCE_ONLINE_FEE_VISIBILITY: MaltaFullTimeFeeVisibility = {
  tuitionFee: false,
  acommodation: false,
  medicalInsurance: false,
  residencePermit: false,
  admissionFee: true,
  otherExpenses: true,
  transfer: false,
  certificationEqualization: false,
  completeOneYear: true,
};

export const MALTA_DISTANCE_ONLINE_FEE_VALUES: Record<MaltaFullTimeFeeLineKey, string> = {
  tuitionFee: "",
  acommodation: "",
  medicalInsurance: "",
  residencePermit: "",
  admissionFee: "1 000",
  otherExpenses: "400",
  transfer: "",
  certificationEqualization: "",
  completeOneYear: "",
};

export const MALTA_DISTANCE_ONLINE_NOTICE_DEFAULTS = {
  showSecondYear: false,
  showTransferFeeNotice: false,
  showAdmissionLetterNotice: false,
  showFirstYearPaymentConfirmation: false,
} as const;

type SetMaltaDistanceFormValue = (
  name: string,
  value: string | boolean,
  options?: { shouldDirty?: boolean },
) => void;

export const applyMaltaDistanceOnlineFormDefaults = (setValue: SetMaltaDistanceFormValue): void => {
  (Object.entries(MALTA_DISTANCE_ONLINE_FEE_VALUES) as Array<[MaltaFullTimeFeeLineKey, string]>).forEach(
    ([key, value]) => {
      setValue(key, value, { shouldDirty: false });
    },
  );

  (Object.entries(MALTA_DISTANCE_ONLINE_FEE_VISIBILITY) as Array<[MaltaFullTimeFeeLineKey, boolean]>).forEach(
    ([key, visible]) => {
      setValue(MALTA_FULL_TIME_FEE_VISIBILITY_FIELD[key], visible, { shouldDirty: false });
    },
  );

  Object.entries(MALTA_DISTANCE_ONLINE_NOTICE_DEFAULTS).forEach(([key, value]) => {
    setValue(key, value, { shouldDirty: false });
  });
};

export type MaltaDistanceNoticeKey = MaltaFullTimeNoticeKey;
